import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Car, Gauge, AlertTriangle, CheckCircle, Clock } from "lucide-react";

const emptyVehicle = {
  registration_number: "", make: "", model: "", year: "", vehicle_type: "",
  status: "active", fuel_type: "diesel", mileage: 0, last_service_date: "",
  next_service_date: "", insurance_expiry: "", rego_expiry: "", ctp_expiry: "",
  nhvr_expiry: "", roadworthy_expiry: "", fire_extinguisher_expiry: "", assigned_driver: ""
};

// Helper to get expiry status color
const getExpiryStatus = (dateStr) => {
  if (!dateStr) return { color: "text-zinc-500", bg: "bg-zinc-500/10", status: "none", icon: null };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(dateStr);
  const daysUntil = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (daysUntil < 0) return { color: "text-red-500", bg: "bg-red-500/10", status: "expired", icon: AlertTriangle, days: daysUntil };
  if (daysUntil <= 7) return { color: "text-amber-500", bg: "bg-amber-500/10", status: "expiring", icon: Clock, days: daysUntil };
  return { color: "text-emerald-500", bg: "bg-emerald-500/10", status: "valid", icon: CheckCircle, days: daysUntil };
};

const ExpiryBadge = ({ date, label }) => {
  const { color, bg, status, icon: Icon, days } = getExpiryStatus(date);
  if (status === "none") return <span className="text-zinc-600 text-xs">Not set</span>;
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm ${bg}`}>
      {Icon && <Icon className={`w-3 h-3 ${color}`} />}
      <span className={`text-xs font-medium ${color}`}>
        {status === "expired" ? `Expired ${Math.abs(days)}d ago` : status === "expiring" ? `${days}d left` : `${days}d`}
      </span>
    </div>
  );
};

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyVehicle });

  const fetchVehicles = () => {
    axios.get(`${API}/vehicles`).then(res => { setVehicles(res.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchVehicles(); }, []);

  const handleSave = async () => {
    if (!form.registration_number) { toast.error("Registration number required"); return; }
    try {
      if (editing) { await axios.put(`${API}/vehicles/${editing.id}`, form); toast.success("Updated"); }
      else { await axios.post(`${API}/vehicles`, form); toast.success("Vehicle added"); }
      setDialogOpen(false); setEditing(null); setForm({ ...emptyVehicle }); fetchVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try { await axios.delete(`${API}/vehicles/${id}`); toast.success("Deleted"); fetchVehicles(); } catch { toast.error("Failed"); }
  };

  const openEdit = (v) => { setEditing(v); setForm({ ...v }); setDialogOpen(true); };
  const openNew = () => { setEditing(null); setForm({ ...emptyVehicle }); setDialogOpen(true); };

  return (
    <div data-testid="vehicles-page">
      <PageHeader title="Vehicles" subtitle={`${vehicles.length} vehicles`}>
        <button data-testid="create-vehicle-button" onClick={openNew}
          className="h-8 px-3 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] flex items-center gap-1.5 transition-colors active:scale-95">
          <Plus className="w-3.5 h-3.5" /> Add Vehicle
        </button>
      </PageHeader>

      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-20">
            <Car className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">No vehicles yet.</p>
          </div>
        ) : (
          <div className="border border-zinc-800 rounded-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[1200px]">
              <thead>
                <tr className="bg-zinc-900/50 border-b border-zinc-800">
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Reg. No.</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Vehicle</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Type</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Fuel</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Mileage</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Rego</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">CTP</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">NHVR</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Roadworthy</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.id} className="border-b border-zinc-800/30 hover:bg-zinc-800/20" data-testid={`vehicle-row-${v.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-orange-400">{v.registration_number}</td>
                    <td className="px-4 py-2.5 text-zinc-200">{v.make} {v.model} {v.year}</td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs">{v.vehicle_type || "—"}</td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs capitalize">{v.fuel_type || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-300 whitespace-nowrap">
                      <span className="flex items-center gap-1"><Gauge className="w-3 h-3 text-zinc-500" />{v.mileage?.toLocaleString() || 0} km</span>
                    </td>
                    <td className="px-4 py-2.5"><ExpiryBadge date={v.rego_expiry} label="Rego" /></td>
                    <td className="px-4 py-2.5"><ExpiryBadge date={v.ctp_expiry} label="CTP" /></td>
                    <td className="px-4 py-2.5"><ExpiryBadge date={v.nhvr_expiry} label="NHVR" /></td>
                    <td className="px-4 py-2.5"><ExpiryBadge date={v.roadworthy_expiry} label="Roadworthy" /></td>
                    <td className="px-4 py-2.5"><StatusBadge status={v.status} /></td>
                    <td className="px-4 py-2.5 text-right">
                      <button data-testid={`edit-vehicle-${v.id}`} onClick={() => openEdit(v)} className="text-zinc-500 hover:text-zinc-200 p-1 mr-1"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`delete-vehicle-${v.id}`} onClick={() => handleDelete(v.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#18181B] border-zinc-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold tracking-tight">{editing ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Basic Info */}
            <div className="border-b border-zinc-800 pb-3">
              <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Vehicle Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Reg. Number *</Label>
                  <Input data-testid="vehicle-reg-input" value={form.registration_number} onChange={e => setForm({ ...form, registration_number: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Type</Label>
                  <select data-testid="vehicle-type-select" value={form.vehicle_type} onChange={e => setForm({ ...form, vehicle_type: e.target.value })}
                    className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                    <option value="">Select...</option>
                    <option value="truck">Truck</option>
                    <option value="van">Van</option>
                    <option value="trailer">Trailer</option>
                    <option value="pickup">Pickup</option>
                    <option value="flatbed">Flatbed</option>
                    <option value="sidelifter">Sidelifter</option>
                    <option value="prime_mover">Prime Mover</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Make</Label>
                  <Input data-testid="vehicle-make-input" value={form.make} onChange={e => setForm({ ...form, make: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Model</Label>
                  <Input data-testid="vehicle-model-input" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Year</Label>
                  <Input data-testid="vehicle-year-input" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Fuel</Label>
                  <select data-testid="vehicle-fuel-select" value={form.fuel_type} onChange={e => setForm({ ...form, fuel_type: e.target.value })}
                    className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                    <option value="diesel">Diesel</option>
                    <option value="petrol">Petrol</option>
                    <option value="electric">Electric</option>
                    <option value="cng">CNG</option>
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
                  <select data-testid="vehicle-status-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                    className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                    <option value="active">Active</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Mileage (km)</Label>
                  <Input data-testid="vehicle-mileage-input" type="number" value={form.mileage} onChange={e => setForm({ ...form, mileage: Number(e.target.value) })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
            </div>

            {/* Compliance Section */}
            <div className="border-b border-zinc-800 pb-3">
              <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                Australian Transport Compliance
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Rego Expiry</Label>
                  <Input data-testid="vehicle-rego-expiry" type="date" value={form.rego_expiry} onChange={e => setForm({ ...form, rego_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">CTP Insurance Expiry</Label>
                  <Input data-testid="vehicle-ctp-expiry" type="date" value={form.ctp_expiry} onChange={e => setForm({ ...form, ctp_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">NHVR Accreditation Expiry</Label>
                  <Input data-testid="vehicle-nhvr-expiry" type="date" value={form.nhvr_expiry} onChange={e => setForm({ ...form, nhvr_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Roadworthy Expiry</Label>
                  <Input data-testid="vehicle-roadworthy-expiry" type="date" value={form.roadworthy_expiry} onChange={e => setForm({ ...form, roadworthy_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Fire Extinguisher Expiry</Label>
                  <Input data-testid="vehicle-fire-expiry" type="date" value={form.fire_extinguisher_expiry} onChange={e => setForm({ ...form, fire_extinguisher_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Insurance Expiry</Label>
                  <Input data-testid="vehicle-insurance-date" type="date" value={form.insurance_expiry} onChange={e => setForm({ ...form, insurance_expiry: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
            </div>

            {/* Service Section */}
            <div>
              <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Service Schedule</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Last Service</Label>
                  <Input data-testid="vehicle-service-date" type="date" value={form.last_service_date} onChange={e => setForm({ ...form, last_service_date: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Next Service</Label>
                  <Input data-testid="vehicle-next-service-date" type="date" value={form.next_service_date} onChange={e => setForm({ ...form, next_service_date: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDialogOpen(false)} className="h-9 px-4 border border-zinc-700 text-zinc-300 text-xs font-medium uppercase tracking-wider rounded-[2px] hover:bg-zinc-800">Cancel</button>
              <button data-testid="save-vehicle" onClick={handleSave} className="h-9 px-4 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] active:scale-95">
                {editing ? "Update" : "Add"} Vehicle
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
